import { useCallback, useState } from 'react';
import { handleEvent } from '@/lib/handleEvent';
import { CONTENT_KIND, type ChatMessage, type StreamEvent } from '@/lib/types';
import { parseLine } from '@/lib/utils';
import { AuthService } from '@/services/authService';

export function useStreamedResponse(apiEndpoint: string, sessionId: string) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [postprocessing, setPostprocessing] = useState<{
    zoneCodes?: string[];
    zoneDetected?: boolean;
    plotDetected?: boolean;
  }>({});

  const [followupQuestions, setFollowupQuestions] = useState<string[]>([]);

  const sendMessage = useCallback(
    async (input: string) => {
      const trimmed = input.trim();
      if (!trimmed) return;

      setIsLoading(true);
      setMessages((prev) => [
        ...prev,
        { type: 'user', content: trimmed, kind: CONTENT_KIND.Text },
        {
          type: 'bot',
          content: '__LOADING__',
          isStreaming: true,
          kind: CONTENT_KIND.Text,
        },
      ]);

      const oidcUser = AuthService.getOidcUserFromLocalStorage();

      try {
        const res = await fetch(apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${oidcUser?.access_token}`,
          },
          body: JSON.stringify({ user_prompt: trimmed, session_id: sessionId }),
        });

        if (!res.body) {
          setIsLoading(false);
          setMessages((prev) => {
            const upd = [...prev];
            const lastBot = [...upd].reverse().find((m) => m.type === 'bot' && m.isStreaming);
            if (lastBot) {
              const idx = upd.indexOf(lastBot);
              upd[idx] = {
                ...lastBot,
                content: 'No response body.',
                isStreaming: false,
                kind: CONTENT_KIND.Error,
              };
            }
            return upd;
          });
          return;
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder('utf-8');

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          const lines = chunk.split('\n').filter((line) => line.startsWith('data:'));

          for (const line of lines) {
            const evt = parseLine(line);
            if (!evt) continue;

            handleEvent(evt as StreamEvent, {
              onRunStarted: () => {
                setIsLoading(true);
                setFollowupQuestions([]);
              },
              onRunCompleted: () => {
                setIsLoading(false);
                setMessages((prev) => {
                  const upd = [...prev];
                  const lastBot = [...upd].reverse().find((m) => m.type === 'bot' && m.isStreaming);
                  if (lastBot) {
                    const idx = upd.indexOf(lastBot);
                    upd[idx] = { ...lastBot, isStreaming: false };
                  }
                  return upd;
                });
              },
              onContent: (chunk) => {
                setMessages((prev) => {
                  const upd = [...prev];
                  const lastBot = [...upd].reverse().find((m) => m.type === 'bot' && m.isStreaming);
                  if (lastBot) {
                    const idx = upd.indexOf(lastBot);
                    const current = upd[idx];
                    const base = current.content === '__LOADING__' ? '' : current.content;
                    upd[idx] = {
                      ...current,
                      content: base + chunk,
                      kind: CONTENT_KIND.Text,
                    };
                  }
                  return upd;
                });
              },
              onError: (msg) => {
                setIsLoading(false);
                setMessages((prev) => {
                  const upd = [...prev];
                  const lastBot = [...upd].reverse().find((m) => m.type === 'bot' && m.isStreaming);
                  if (lastBot) {
                    const idx = upd.indexOf(lastBot);
                    upd[idx] = {
                      ...lastBot,
                      content: `Error: ${msg}`,
                      isStreaming: false,
                      kind: CONTENT_KIND.Error,
                    };
                  } else {
                    upd.push({
                      type: 'bot',
                      content: `Error: ${msg}`,
                      kind: CONTENT_KIND.Error,
                    });
                  }
                  return upd;
                });
              },
              onPostprocessing: ({ zoneCodes, zoneDetected, plotDetected }) => {
                setPostprocessing({
                  zoneCodes,
                  zoneDetected,
                  plotDetected,
                });
              },
              onFollowup: ({ content }: { content: string }) => {
                setFollowupQuestions((prev) => [...prev, content]);
              },
            });
          }
        }
      } catch (error) {
        console.error('Error in streaming response:', error);
        setIsLoading(false);
        setMessages((prev) => {
          const upd = [...prev];
          const lastBot = [...upd].reverse().find((m) => m.type === 'bot' && m.isStreaming);
          if (lastBot) {
            const idx = upd.indexOf(lastBot);
            upd[idx] = {
              ...lastBot,
              content: 'Error occurred while processing your request.',
              isStreaming: false,
              kind: CONTENT_KIND.Error,
            };
          } else {
            upd.push({
              type: 'bot',
              content: 'Error occurred while processing your request.',
              kind: CONTENT_KIND.Error,
            });
          }
          return upd;
        });
      } finally {
        setIsLoading(false);
      }
    },
    [apiEndpoint, sessionId],
  );

  const resetConversation = useCallback(() => {
    setMessages([]);
    setIsLoading(false);
    setPostprocessing({});
    setFollowupQuestions([]);
  }, []);

  // Expose postprocessing IDs to the UI
  return {
    messages,
    isLoading,
    sendMessage,
    setMessages,
    setIsLoading,
    postprocessing,
    followupQuestions,
    setFollowupQuestions,
    resetConversation,
  };
}
